/**
 * Path 3: Portfolio Brain — Multi-project intelligence.
 *
 * 4 scenes showing cross-project awareness:
 * portfolio pulse, pattern propagation, dependency intelligence, context switching.
 */

import type { AdventurePath } from './paths';

export const PORTFOLIO_BRAIN_PATH: AdventurePath = {
  id: 'portfolio',
  title: 'Portfolio Brain',
  subtitle: 'Six projects, one intelligence layer',
  scenes: [
    // ── Scene 1: Portfolio Pulse ─────────────────────
    {
      id: 'portfolio-1',
      title: 'Portfolio Pulse',
      lines: [
        { text: '$ cortex projects --health', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Portfolio Health Matrix ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  PROJECT          TESTS    HEALTH   VELOCITY   RISK', style: 'dim' },
        { text: '  -----------------------------------------------------------', style: 'dim' },
        { text: '  Vortex Backend   2,835    98.2%    +23%       LOW', style: 'success' },
        { text: '  Vortex Frontend    113    96.1%    +12%       LOW', style: 'success' },
        { text: '  Cortex             853    97.8%    +31%       LOW', style: 'success' },
        { text: '  Alpha Arena        603    94.3%    +8%        MEDIUM', style: 'warning' },
        { text: '  Pupil            1,255    99.1%    -2%        LOW', style: 'success' },
        { text: '  DJ-CoPilot          41    87.2%    0%         HIGH', style: 'error' },
        { text: '  -----------------------------------------------------------', style: 'dim' },
        { text: '  Portfolio total: 5,700 tests | Avg health: 95.5%', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Alerts:', style: 'header' },
        { text: '  > Alpha Arena: 3 flaky tests in backtest suite', style: 'warning' },
        { text: '  > DJ-CoPilot: No commits in 9 days', style: 'error' },
        { text: '  > Pupil: Phase 2 batches stale (consider archive)', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  Cross-project dependencies:', style: 'header' },
        { text: '  > Vortex -> Cortex: orchestration layer (healthy)', style: 'data' },
        { text: '  > Alpha Arena -> Cortex: pattern matching (healthy)', style: 'data' },
        { text: '  > All -> Cortex: memory + learning (healthy)', style: 'data' },
        { text: '', style: 'blank' },
        { text: 'One command. Six projects. Complete awareness.', style: 'narration' },
        { text: 'No context loading. No "which project was I on?"', style: 'narration' },
      ],
      choices: [
        { label: 'See pattern propagation across projects', next: 'portfolio-2', tag: 'PATTERNS' },
        { label: 'See dependency intelligence', next: 'portfolio-3', tag: 'DEPS' },
      ],
    },

    // ── Scene 2: Pattern Propagation ─────────────────
    {
      id: 'portfolio-2',
      title: 'Pattern Propagation',
      lines: [
        { text: '$ cortex recommendations --propagation', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Patterns That Should Spread ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  1. SQLite WAL Mode (Vortex -> Alpha Arena)', style: 'data' },
        { text: '     Vortex: Deployed. Cache no longer blocks serving.', style: 'success' },
        { text: '     Alpha Arena: Still using default journal mode.', style: 'warning' },
        { text: '     Impact: Would fix the backtest DB lock issues.', style: 'narration' },
        { text: '     Confidence: 0.87', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  2. ThreadPoolExecutor Pattern (Vortex -> Pupil)', style: 'data' },
        { text: '     Vortex: GRIB parallel loading (15s -> 4s)', style: 'success' },
        { text: '     Pupil: Sequential FRED API calls (~8s)', style: 'warning' },
        { text: '     Impact: 3-4x speedup on data ingestion.', style: 'narration' },
        { text: '     Confidence: 0.82', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  3. Strict Test Assertions (All projects)', style: 'data' },
        { text: '     Vortex: Enforced. 0 permissive patterns.', style: 'success' },
        { text: '     Alpha Arena: 3 tests still use status in [200,500]', style: 'error' },
        { text: '     Impact: Prevents silent test rot.', style: 'narration' },
        { text: '     Confidence: 0.95', style: 'dim' },
        { text: '', style: 'blank' },
        { text: 'Patterns proven in one project become', style: 'narration' },
        { text: 'recommendations for every project.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'A human would need to remember that Vortex', style: 'accent' },
        { text: 'solved this same problem 3 weeks ago.', style: 'accent' },
        { text: 'Cortex just... knows.', style: 'accent' },
      ],
      hubEvent: {
        task: 'Pattern propagation analysis',
        complexity: 'moderate',
        selectedProvider: 'DeepSeek',
        selectedModel: 'V3.2',
        cost: 0.014,
        baselineCost: 0.21,
        reason: 'Pattern matching is DeepSeek\'s strength. No need for Sonnet.',
      },
      choices: [
        { label: 'See dependency intelligence', next: 'portfolio-3', tag: 'DEPS' },
        { label: 'See context switching cost', next: 'portfolio-4', tag: 'SWITCHING' },
      ],
    },

    // ── Scene 3: Dependency Intelligence ─────────────
    {
      id: 'portfolio-3',
      title: 'Dependency Intelligence',
      lines: [
        { text: '$ cortex anomalies --deps', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Cross-Project Dependency Drift ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Shared dependency: FastAPI', style: 'data' },
        { text: '  Vortex Backend:  0.115.0 (latest)', style: 'success' },
        { text: '  Alpha Arena:     0.109.0 (3 versions behind)', style: 'warning' },
        { text: '  Cortex:          0.115.0 (latest)', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  Shared dependency: pytest', style: 'data' },
        { text: '  All projects:    8.3.4 (in sync)', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  Shared dependency: xarray', style: 'data' },
        { text: '  Vortex Backend:  2024.12.0', style: 'success' },
        { text: '  Pupil:           2024.09.0 (3 months behind)', style: 'warning' },
        { text: '  Risk: API change in 2024.11 affects .interp()', style: 'error' },
        { text: '', style: 'blank' },
        { text: '  Internal dependency: Cortex bridge', style: 'data' },
        { text: '  Version: consistent across 4 consumers', style: 'success' },
        { text: '  Last breaking change: 42 days ago (handled)', style: 'dim' },
        { text: '', style: 'blank' },
        { text: 'Dep drift is invisible until it breaks.', style: 'narration' },
        { text: 'Cortex watches it continuously.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'That xarray drift? It will break Pupil\'s', style: 'accent' },
        { text: 'interp() calls. Cortex knows this because', style: 'accent' },
        { text: 'Vortex already hit the same bug.', style: 'accent' },
      ],
      choices: [
        { label: 'See context switching cost analysis', next: 'portfolio-4', tag: 'SWITCHING' },
        { label: 'Explore another path', next: 'hub', tag: 'PATHS' },
      ],
    },

    // ── Scene 4: Context Switching Cost ──────────────
    {
      id: 'portfolio-4',
      title: 'Context Switching Cost',
      lines: [
        { text: '$ cortex metrics --context-switching', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Context Switching Analysis (30 days) ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Without Cortex (estimated baseline):', style: 'dim' },
        { text: '  Avg context load time:   12 min/switch', style: 'data' },
        { text: '  Switches per day:        3.2', style: 'data' },
        { text: '  Daily switching tax:     38 min/day', style: 'warning' },
        { text: '  Monthly switching tax:   13.3 hours', style: 'error' },
        { text: '', style: 'blank' },
        { text: '  With Cortex:', style: 'header' },
        { text: '  Avg context load time:   45 sec/switch', style: 'success' },
        { text: '  (Briefing + memory + last session state)', style: 'dim' },
        { text: '  Switches per day:        3.2', style: 'data' },
        { text: '  Daily switching tax:     2.4 min/day', style: 'success' },
        { text: '  Monthly switching tax:   0.84 hours', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  Time recovered: 12.5 hours/month', style: 'success' },
        { text: '  That is 1.5 engineering days.', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '  How:', style: 'header' },
        { text: '  > GOALS.md = strategy (what to do)', style: 'data' },
        { text: '  > MEMORY.md = ops cache (how things work)', style: 'data' },
        { text: '  > Session state = where you left off', style: 'data' },
        { text: '  > Intelligence = what you should know', style: 'data' },
        { text: '', style: 'blank' },
        { text: 'The most expensive thing in software', style: 'narration' },
        { text: 'engineering is not compute. It is the 12', style: 'narration' },
        { text: 'minutes it takes a human to remember what', style: 'narration' },
        { text: 'they were doing.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'Cortex makes that 45 seconds.', style: 'accent' },
      ],
      choices: [
        { label: 'Explore another path', next: 'hub', tag: 'PATHS' },
        { label: 'See the future vision', next: 'future-1', tag: 'FUTURE' },
      ],
    },
  ],
};
